import { nextTick, onMounted, watch } from "vue";

import { isIntercomReady } from "@/analytics/impl/intercom";
import { getExperimentVariant } from "@/analytics/impl/posthog";
import type { ModalName } from "@/components/modal/modals";
import { captureMessage } from "@/error/sentry";
import { INTERCOM_TOURS } from "@/model/onboardingGuide";
import { useModalStore } from "@/store/modal";
import type { OnboardingStepId } from "@/store/onboarding";
import { useOnboardingStore } from "@/store/onboarding";

/**
 * Two-step onboarding flow:
 * 1. Welcome video
 * 2. Interactive guide, with two variants (run as an a/b test)
 *  2.a Onboarding guide modal (control)
 *  2.b Quick tour
 */
const onboardingSteps: Record<OnboardingStepId, () => void> = {
  "welcome-video": () => {
    useModalStore().open("OnboardingVideoModal");

    // Prefetch the variant for the next step
    getExperimentVariant("onboarding-guide");
  },
  guide: () => {
    const variant = getExperimentVariant("onboarding-guide");

    if (variant !== "tour") {
      // Control or variant not yet assigned — show the guide modal
      // (favour a strong onboarding experience over a perfectly clean experiment)
      return useModalStore().open("OnboardingGuideModal");
    }

    // Guard if Intercom isn't loaded for whatever reason
    if (!isIntercomReady()) {
      captureMessage("Intercom not ready for onboarding tour. Falling back to guide modal.");
      return useModalStore().open("OnboardingGuideModal");
    }

    // Start the tour variant
    window.Intercom("startTour", INTERCOM_TOURS.quick_start);
    useOnboardingStore().completeStep("guide"); // Intercom does not notify us when the tour is completed...
  },
};

const ONBOARDING_MODALS: ReadonlySet<ModalName> = new Set([
  "OnboardingVideoModal",
  "OnboardingGuideModal",
  "OnboardingVideoReplayModal",
]);

export function useOnboardingFlow() {
  onMounted(async () => {
    await nextTick();
    runCurrentStep();
  });

  /** When an onboarding-related modal is closed, check if we need to open the next one (based on step in the store) */
  watch(
    () => useModalStore().modal?.name,
    (current, previous) => {
      if (!current && previous && ONBOARDING_MODALS.has(previous)) {
        runCurrentStep();
      }
    },
  );

  function runCurrentStep() {
    const stepId = useOnboardingStore().currentStep;
    if (stepId) {
      onboardingSteps[stepId]();
    }
  }
}
